import { DAYS } from "../../constants";
import { Address, Location } from "../../types";
import { normalizeCadence } from "../../components/cadence";
import { normalizePhotoAssets } from "../assets";

const dayKey = (val: any) => {
  if (!val) return null;
  const str = String(val);
  const parts = str.split(".");
  const raw = parts.length > 1 ? parts[parts.length - 1] : str;
  return DAYS.find((d) => d.toLowerCase().startsWith(raw.toLowerCase())) || raw;
};

export const normalizeServiceCadenceResponse = (sc: any) =>
  sc
    ? normalizeCadence({
        frequency: sc.frequency,
        daysBetween: sc.frequency,
        machineThresholdScope: sc.machine_threshold_scope ?? sc.machineThresholdScope ?? "entire_location",
        requireDynamicTrigger: sc.require_dynamic_trigger ?? sc.requireDynamicTrigger ?? false,
        revenueThreshold: sc.revenue_threshold ?? sc.revenueThreshold,
        cashThreshold: sc.cash_threshold ?? sc.cashThreshold,
        cardThreshold: sc.card_threshold ?? sc.cardThreshold,
        inventoryTrigger: sc.inventory_trigger,
        days: (sc.days || []).map(dayKey).filter(Boolean) as string[],
      })
    : undefined;

export const normalizeServiceCadencePayload = (
  serviceCadence: Location["serviceCadence"],
) => {
  if (!serviceCadence) return undefined;
  const normalized = normalizeCadence(serviceCadence);
  const shouldIncludeAllDays =
    normalized.frequency === "daily" || normalized.daysBetween === 1;
  const revenueThreshold = normalized.revenueThreshold ?? null;
  const inventoryTrigger = normalized.inventoryTrigger ?? null;
  return {
    frequency: normalized.daysBetween,
    machine_threshold_scope: normalized.machineThresholdScope,
    require_dynamic_trigger: normalized.requireDynamicTrigger,
    revenue_threshold: revenueThreshold,
    cash_threshold: normalized.cashThreshold ?? null,
    card_threshold: normalized.cardThreshold ?? null,
    inventory_trigger: inventoryTrigger,
    days: (shouldIncludeAllDays ? DAYS : normalized.days || [])
      .map((d) => dayKey(d))
      .filter(Boolean),
  };
};

export const normalizeHoursPayload = (
  hours?: Record<string, { open?: string; close?: string; closed?: boolean }>,
) => {
  if (!hours) return undefined;
  return DAYS.reduce<Record<string, any>>((acc, day) => {
    const entry = hours[day] || {};
    const closed =
      entry.closed !== undefined ? entry.closed : !entry.open && !entry.close;
    acc[day] = {
      day,
      open: closed || !entry.open ? undefined : entry.open,
      close: closed || !entry.close ? undefined : entry.close,
      closed,
    };
    return acc;
  }, {});
};

export const normalizeCommissionPayload = (
  commission?: Location["commission"],
) => {
  if (!commission?.type) return undefined;
  if (commission.type === "none") return null;
  return {
    type: commission!.type,
    payment_type:
      commission!.payment_type !== undefined
        ? commission!.payment_type
        : undefined,
    value:
      commission!.type !== "monthly" &&
      commission!.value !== undefined && commission!.value !== null
        ? Number(commission!.value)
        : undefined,
    fixed_amount_cents:
      commission!.type === "monthly" ? commission!.fixedAmountCents : undefined,
    rounding_mode:
      commission!.type === "%_gross_sales" ||
      commission!.type === "%_gross_profit"
        ? commission!.roundingMode || "none"
        : "none",
    profit_margin_percent:
      commission!.profitMarginPercent !== undefined &&
      commission!.profitMarginPercent !== null
        ? Number(commission!.profitMarginPercent)
        : undefined,
    exclude_refunds_from_commission:
      commission!.excludeRefundsFromCommission !== undefined
        ? commission!.excludeRefundsFromCommission
        : undefined,
    exclude_card_transaction_surcharge_from_commission:
      commission!.excludeCardTransactionSurchargeFromCommission !== undefined
        ? commission!.excludeCardTransactionSurchargeFromCommission
        : undefined,
    payout_trigger: commission.payoutTrigger ?? "every_collection",
    payout_interval_count: commission.payoutIntervalCount ?? undefined,
    payout_interval_unit: commission.payoutIntervalUnit ?? undefined,
    payout_anchor_date: commission.payoutAnchorDate ?? undefined,
    payout_threshold_cents: commission.payoutThresholdCents ?? undefined,
    bank_settings: commission.bankSettings ?? undefined,
    revision: commission.revision,
  };
};

export const normalizeCommissionResponse = (
  commission: any,
): Location["commission"] => {
  const bank = commission?.bank_settings ?? commission?.bankSettings;
  return commission
    ? {
        ...commission,
        payment_type:
          commission.payment_type ?? commission.paymentType ?? undefined,
        fixedAmountCents:
          commission.fixed_amount_cents ?? commission.fixedAmountCents,
        roundingMode:
          commission.rounding_mode ?? commission.roundingMode ?? "none",
        profitMarginPercent:
          commission.profit_margin_percent ?? commission.profitMarginPercent,
        lastCommissionPaidAt:
          commission.last_commission_paid_at ?? commission.lastCommissionPaidAt,
        excludeRefundsFromCommission:
          commission.exclude_refunds_from_commission ??
          commission.excludeRefundsFromCommission,
        excludeCardTransactionSurchargeFromCommission:
          commission.exclude_card_transaction_surcharge_from_commission ??
          commission.excludeCardTransactionSurchargeFromCommission ??
          true,
        payoutTrigger:
          commission.payout_trigger ??
          commission.payoutTrigger ??
          "every_collection",
        payoutIntervalCount:
          commission.payout_interval_count ??
          commission.payoutIntervalCount ??
          null,
        payoutIntervalUnit:
          commission.payout_interval_unit ??
          commission.payoutIntervalUnit ??
          null,
        payoutAnchorDate:
          commission.payout_anchor_date ??
          commission.payoutAnchorDate ??
          null,
        payoutThresholdCents:
          commission.payout_threshold_cents ??
          commission.payoutThresholdCents ??
          null,
        bankSettings: bank ? {
          paymentApproval: bank.payment_approval ?? bank.paymentApproval ?? "manual",
          automaticPaymentLimitCents: bank.automatic_payment_limit_cents ?? bank.automaticPaymentLimitCents ?? null,
          fundingAccountId: bank.funding_account_id ?? bank.fundingAccountId ?? null,
          payoutAccountId: bank.payout_account_id ?? bank.payoutAccountId ?? null,
        } : null,
        revision: commission.revision,
      }
    : undefined;
};

export const normalizeAddressPayload = (address?: Address | null) =>
  address && address.street && address.city && address.country
    ? {
        ...address,
        state: address.state || "",
        zip: address.zip ? String(address.zip) : "",
      }
    : null;

export const normalizeLocationResponse = (loc: any): Location => ({
  ...loc,
  name: loc?.name,
  isPublic: loc?.is_public,
  defaultAssigneeId:
    loc?.defaultAssignee ??
    loc?.default_assignee ??
    loc?.default_assignee_id ??
    loc?.defaultAssigneeId ??
    null,
  contactTransactionVisibility:
    loc?.contact_transaction_visibility ??
    loc?.contactTransactionVisibility ??
    "hidden",
  contactFinancialVisibility:
    loc?.contact_financial_visibility ??
    loc?.contactFinancialVisibility ??
    "commission_only",
  contactRefillInventoryVisible:
    loc?.contact_refill_inventory_visible ??
    loc?.contactRefillInventoryVisible ??
    false,
  contactTaskMetricsVisible:
    loc?.contact_task_metrics_visible ??
    loc?.contactTaskMetricsVisible ??
    false,
  contactTaskPhotoVisible:
    loc?.contact_task_photo_visible ??
    loc?.contactTaskPhotoVisible ??
    true,
  contactLocationPhotoVisible:
    loc?.contact_location_photo_visible ??
    loc?.contactLocationPhotoVisible ??
    true,
  contactTaskUpdateEmailsEnabled:
    loc?.contact_task_update_emails_enabled ??
    loc?.contactTaskUpdateEmailsEnabled ??
    true,
  revenueSchedule: loc?.revenue_schedule ?? loc?.revenueSchedule ?? "tomorrow",
  createdAt: loc?.createdAt ?? loc?.created_at ?? null,
  updatedAt: loc?.updatedAt ?? loc?.updated_at ?? null,
  contacts: Array.isArray(loc?.contacts)
    ? loc.contacts.map((c: any) => ({
        id: c?.id,
        userId: c?.uid,
        active: c?.active ?? undefined,
        inviteStatus: c?.inviteStatus ?? c?.invite_status,
        role: c?.role,
        firstName: c?.firstName ?? c?.first_name ?? "",
        lastName: c?.lastName ?? c?.last_name ?? "",
        email: c?.email ?? "",
        phone: c?.phone ?? "",
      }))
    : [],
  commission: normalizeCommissionResponse(loc?.commission),
  machineAssignmentInfo: (() => {
    const info = loc?.machine_assignment_info ?? loc?.machineAssignmentInfo;
    if (!info) return undefined;
    return Object.fromEntries(
      Object.entries(info).map(([machineId, value]) => {
        const assignedAt =
          (value as any)?.assigned_at ?? (value as any)?.assignedAt;
        const unassignedAt =
          (value as any)?.unassigned_at ?? (value as any)?.unassignedAt;
        let normalizedAssignedAt: string | undefined;
        if (assignedAt) {
          const parsed = new Date(String(assignedAt));
          normalizedAssignedAt = Number.isNaN(parsed.getTime())
            ? String(assignedAt)
            : parsed.toISOString();
        }
        let normalizedUnassignedAt: string | null | undefined = null;
        if (unassignedAt) {
          const parsed = new Date(String(unassignedAt));
          normalizedUnassignedAt = Number.isNaN(parsed.getTime())
            ? String(unassignedAt)
            : parsed.toISOString();
        }
        return [
          machineId,
          {
            ...(value as any),
            assignedAt: normalizedAssignedAt,
            unassignedAt: normalizedUnassignedAt,
          },
        ];
      }),
    );
  })(),
  machineAssignmentHistory: (() => {
    const info =
      loc?.machine_assignment_history ?? loc?.machineAssignmentHistory;
    if (!info) return undefined;
    return Object.fromEntries(
      Object.entries(info).map(([machineId, entries]) => {
        const list = Array.isArray(entries) ? entries : [];
        const normalized = list.map((value: any) => {
          const assignedAt = value?.assigned_at ?? value?.assignedAt;
          const unassignedAt = value?.unassigned_at ?? value?.unassignedAt;
          let normalizedAssignedAt: string | undefined;
          if (assignedAt) {
            const parsed = new Date(String(assignedAt));
            normalizedAssignedAt = Number.isNaN(parsed.getTime())
              ? String(assignedAt)
              : parsed.toISOString();
          }
          let normalizedUnassignedAt: string | null | undefined = null;
          if (unassignedAt) {
            const parsed = new Date(String(unassignedAt));
            normalizedUnassignedAt = Number.isNaN(parsed.getTime())
              ? String(unassignedAt)
              : parsed.toISOString();
          }
          return {
            ...(value as any),
            assignedAt: normalizedAssignedAt,
            unassignedAt: normalizedUnassignedAt,
          };
        });
        return [machineId, normalized];
      }),
    );
  })(),
  status: loc?.status ?? (loc?.discontinued ? "discontinued" : "active"),
  onHoldAt: loc?.on_hold_at ?? loc?.onHoldAt ?? null,
  totalOnHoldSeconds: Number(loc?.total_on_hold_seconds ?? loc?.totalOnHoldSeconds ?? 0),
  daysInService: Number(loc?.days_in_service ?? loc?.daysInService ?? 0),
  discontinuedAt: loc?.discontinued_at ?? loc?.discontinuedAt ?? null,
  discontinued: loc?.status
    ? loc.status === "discontinued"
    : Boolean(loc?.discontinued),
  serviceCadence: normalizeServiceCadenceResponse(loc?.service_cadence),
  assets: normalizePhotoAssets(loc?.assets),
});

export const buildLocationPayload = (
  payload: Location,
  opts?: { includeAddress?: boolean; includeMachines?: boolean },
) => {
  const body: any = {
    name: payload.name,
    hours: normalizeHoursPayload(payload.hours),
    notes: payload.notes ?? null,
  };
  if (payload.defaultAssigneeId !== undefined) {
    body.default_assignee_id = payload.defaultAssigneeId || null;
  }
  if (payload.contactTransactionVisibility !== undefined) {
    body.contact_transaction_visibility = payload.contactTransactionVisibility;
  }
  if (payload.contactFinancialVisibility !== undefined) {
    body.contact_financial_visibility = payload.contactFinancialVisibility;
  }
  if (payload.contactRefillInventoryVisible !== undefined) {
    body.contact_refill_inventory_visible = payload.contactRefillInventoryVisible;
  }
  if (payload.contactTaskMetricsVisible !== undefined) {
    body.contact_task_metrics_visible = payload.contactTaskMetricsVisible;
  }
  if (payload.contactTaskPhotoVisible !== undefined) {
    body.contact_task_photo_visible = payload.contactTaskPhotoVisible;
  }
  if (payload.contactLocationPhotoVisible !== undefined) {
    body.contact_location_photo_visible = payload.contactLocationPhotoVisible;
  }
  if (payload.contactTaskUpdateEmailsEnabled !== undefined) {
    body.contact_task_update_emails_enabled = payload.contactTaskUpdateEmailsEnabled;
  }

  if (opts?.includeMachines !== false) {
    body.machines = Array.from(
      new Set(
        (payload.machines || [])
          .map((m: any) => (typeof m === "string" ? m : m?.id))
          .filter(Boolean),
      ),
    );
  }
  if (opts?.includeAddress !== false) {
    body.address = normalizeAddressPayload(payload.address);
  }

  const serviceCadence = normalizeServiceCadencePayload(payload.serviceCadence);
  const commission = normalizeCommissionPayload(payload.commission);
  if (serviceCadence) body.service_cadence = serviceCadence;
  if (commission !== undefined) body.commission = commission;
  body.revenue_schedule = payload.revenueSchedule ?? "same_day";

  return body;
};
